import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle, ClipboardList, RefreshCw } from "lucide-react";
import Header from "../component/header";
import Footer from "../component/Footer";
import Banner from "../component/Banner";
import BannerImg from "../image/gallaryBanner.png";

const WORD_LIMIT = 200;

const countWords = (text) =>
  text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const ReviewerDashboard = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ type: "", message: "" });
  const [feedbackState, setFeedbackState] = useState({});
  const [submitting, setSubmitting] = useState(null);
  const fetchedRef = useRef(false);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/reviewer/assignments", {
        withCredentials: true,
      });
      if (data.success) {
        setAssignments(data.assignments || []);
        const initial = {};
        data.assignments.forEach((a) => {
          initial[a._id] = a.feedback?.feedback || "";
        });
        setFeedbackState(initial);
      }
    } catch (err) {
      setNotice({
        type: "error",
        message: err?.response?.data?.message || "Could not load assignments.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchAssignments();
    }
  }, [fetchAssignments]);

  const handleFeedbackChange = (assignmentId, value) => {
    setFeedbackState((prev) => ({ ...prev, [assignmentId]: value }));
  };

  const handleSubmitFeedback = async (assignmentId) => {
    const text = feedbackState[assignmentId] || "";
    const wc = countWords(text);

    if (!text.trim()) {
      setNotice({ type: "error", message: "Feedback cannot be empty." });
      return;
    }
    if (wc > WORD_LIMIT) {
      setNotice({
        type: "error",
        message: `Feedback must not exceed ${WORD_LIMIT} words. You used ${wc}.`,
      });
      return;
    }

    setSubmitting(assignmentId);
    setNotice({ type: "", message: "" });
    try {
      const { data } = await axios.post(
        `/api/reviewer/assignments/${assignmentId}/feedback`,
        { feedback: text },
        { withCredentials: true }
      );
      if (data.success) {
        setNotice({ type: "success", message: "Feedback saved successfully." });
        setAssignments((prev) =>
          prev.map((a) =>
            a._id === assignmentId
              ? { ...a, feedback: { feedback: text, wordCount: wc } }
              : a
          )
        );
      }
    } catch (err) {
      setNotice({
        type: "error",
        message: err?.response?.data?.message || "Failed to save feedback.",
      });
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div>
      <Header />
      <Banner backgroundImage={BannerImg} heading="Reviewer Dashboard" minHeight="40vh" padding="80px 0" />

      <section className="main-lwbl rounded-[30px] bg-[#F9FAFB] px-4 py-16">
        <div className="mx-auto max-w-4xl">

          <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                <ClipboardList size={24} />
              </div>
              <div>
                <p className="font-outfit text-sm font-semibold uppercase tracking-[0.18em] text-cyan-600">
                  Reviewer workspace
                </p>
                <h2 className="mt-1 font-cinzel text-2xl font-bold text-gray-900">
                  My Assignments
                </h2>
                <p className="mt-1 font-outfit text-sm text-gray-500">
                  Review assigned submissions and submit feedback (max {WORD_LIMIT} words).
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchAssignments}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 font-outfit text-sm font-semibold text-gray-700 hover:border-cyan-300 hover:text-cyan-700"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>

          {notice.message && (
            <div
              className={`mb-5 rounded-2xl border px-4 py-3 font-outfit text-sm ${
                notice.type === "error"
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700"
              }`}
            >
              {notice.message}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-cyan-400 border-t-transparent" />
            </div>
          ) : assignments.length === 0 ? (
            <div className="rounded-3xl border border-gray-100 bg-white px-6 py-20 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 text-gray-300">
                <ClipboardList size={30} />
              </div>
              <h3 className="font-cinzel text-xl font-bold text-gray-800">
                No assignments yet
              </h3>
              <p className="mx-auto mt-2 max-w-sm font-outfit text-sm text-gray-500">
                Submissions assigned to you will appear here.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {assignments.map((assignment) => {
                const submission = assignment.submission || {};
                const text = feedbackState[assignment._id] || "";
                const wc = countWords(text);
                const remaining = WORD_LIMIT - wc;
                const isOverLimit = wc > WORD_LIMIT;
                const isSubmitting = submitting === assignment._id;
                const hasFeedback = !!assignment.feedback;

                return (
                  <article
                    key={assignment._id}
                    className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm"
                  >
                    <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-cinzel text-lg font-bold text-gray-900">
                            {submission.title || "Untitled submission"}
                          </h3>
                          <p className="mt-1 font-outfit text-sm text-gray-500">
                            By {submission.user?.memberId || "Unknown"} ·{" "}
                            <span className="capitalize">{submission.status || "pending"}</span>
                          </p>
                        </div>
                        {hasFeedback && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 font-outfit text-xs font-semibold text-emerald-700">
                            <CheckCircle size={13} />
                            Feedback submitted
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="px-6 py-5">
                      {submission.description && (
                        <p className="mb-4 font-outfit text-sm leading-6 text-gray-600">
                          {submission.description}
                        </p>
                      )}

                      <label className="mb-1.5 block font-outfit text-sm font-semibold text-gray-700">
                        Your feedback
                      </label>
                      <textarea
                        rows={5}
                        value={text}
                        onChange={(e) =>
                          handleFeedbackChange(assignment._id, e.target.value)
                        }
                        placeholder="Write your feedback here..."
                        className={`w-full rounded-2xl border px-4 py-3 font-outfit text-sm leading-6 outline-none transition-colors ${
                          isOverLimit
                            ? "border-red-300 bg-red-50 focus:border-red-400"
                            : "border-gray-200 bg-gray-50 focus:border-cyan-300 focus:bg-white"
                        }`}
                      />
                      <div className="mt-1.5 flex items-center justify-between">
                        <span
                          className={`font-outfit text-xs ${
                            isOverLimit ? "font-semibold text-red-600" : "text-gray-400"
                          }`}
                        >
                          {isOverLimit
                            ? `${Math.abs(remaining)} words over limit`
                            : `${remaining} words remaining`}
                        </span>
                        <span className="font-outfit text-xs text-gray-400">
                          {wc} / {WORD_LIMIT}
                        </span>
                      </div>

                      <button
                        type="button"
                        disabled={isOverLimit || isSubmitting || !text.trim()}
                        onClick={() => handleSubmitFeedback(assignment._id)}
                        className="mt-4 inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 font-outfit text-sm font-semibold text-white hover:bg-cyan-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <CheckCircle size={15} />
                        )}
                        {hasFeedback ? "Update feedback" : "Submit feedback"}
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReviewerDashboard;
